import { useEffect, useState } from "react";
import { Eye, ThumbsUp, RotateCcw, Lightbulb, MessageSquareQuote, Loader2, Wand2, Timer, Play, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError, runJob } from "@/lib/api";

function TimerBar({ phase, left, limit, taken, index, onStart, onStop }) {
  const pct = (left / limit) * 100;
  const urgent = phase === "running" && left <= 10;
  return (
    <div data-testid={`interview-timer-${index}`} className={`mt-4 flex flex-wrap items-center gap-3 rounded-xl border p-3 ${urgent ? "border-red-300 bg-red-50" : "border-line bg-canvas-2"}`}>
      <Timer className={`h-5 w-5 ${urgent ? "animate-pulse text-red-600" : "text-ink"}`} />
      <span data-testid={`interview-timer-display-${index}`} className={`font-mono text-lg font-semibold ${urgent ? "text-red-600" : "text-ink"}`}>
        {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
      </span>
      <div className="h-1.5 min-w-[80px] flex-1 overflow-hidden rounded-full bg-line">
        <div className={`h-full transition-[width] duration-1000 ease-linear ${urgent ? "bg-red-500" : "bg-aqua-solid"}`} style={{ width: `${pct}%` }} />
      </div>
      {phase === "idle" && <Button size="sm" data-testid={`interview-timer-start-${index}`} onClick={onStart} className="rounded-full"><Play className="mr-1.5 h-3.5 w-3.5" />Start timer</Button>}
      {phase === "running" && <Button size="sm" variant="outline" data-testid={`interview-timer-stop-${index}`} onClick={onStop} className="rounded-full"><Square className="mr-1.5 h-3.5 w-3.5" />I'm done</Button>}
      {phase === "done" && (
        <>
          <span data-testid={`interview-timer-result-${index}`} className="text-xs text-ink-2">Answered in {taken}s of {limit}s</span>
          <Button size="sm" variant="ghost" data-testid={`interview-timer-retry-${index}`} onClick={onStart} className="rounded-full"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Try again</Button>
        </>
      )}
    </div>
  );
}

const List = ({ title, items, color }) => items.length > 0 && (
  <div>
    <p className={`mb-1.5 text-xs font-semibold uppercase tracking-wider ${color}`}>{title}</p>
    <ul className="space-y-1 text-sm text-ink-2">{items.map((t, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ink-3" />{t}</li>)}</ul>
  </div>
);

function FeedbackPanel({ fb, index }) {
  const color = fb.score >= 8 ? "text-emerald-600 border-emerald-200" : fb.score >= 5 ? "text-amber-600 border-amber-200" : "text-red-600 border-red-200";
  return (
    <div data-testid={`interview-feedback-${index}`} className="mt-4 rounded-xl border border-line bg-surface p-5 fade-up">
      <div className="flex items-center gap-4">
        <span data-testid={`interview-feedback-score-${index}`} className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 font-display text-lg font-extrabold ${color}`}>{fb.score}/10</span>
        <p className="text-sm font-medium text-ink">{fb.verdict}</p>
  {fb.time_limit && <span data-testid={`interview-feedback-timing-${index}`} className="ml-auto shrink-0 whitespace-nowrap rounded-full bg-elevated px-2.5 py-1 text-xs text-ink-2">Timed: {fb.time_taken}s / {fb.time_limit}s</span>}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <List title="What you did well" items={fb.strengths} color="text-emerald-700" />
        <List title="How to improve" items={fb.improvements} color="text-amber-700" />
      </div>
      {fb.missing_points.length > 0 && <div className="mt-4"><List title="Points you missed" items={fb.missing_points} color="text-red-700" /></div>}
      {fb.improved_answer && (
        <div className="mt-4 rounded-lg bg-canvas-2 p-4 text-sm leading-relaxed text-ink-2">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-brand">Your answer, polished</p>{fb.improved_answer}
        </div>
      )}
    </div>
  );
}

export default function InterviewCard({ setId, q, index, rating, onRate, feedback, onFeedback, timeLimit, feedbackLocked = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(feedback?.answer || "");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [left, setLeft] = useState(timeLimit || 0);
  const [taken, setTaken] = useState(null);
  const timed = !!timeLimit;

  useEffect(() => {
    setPhase("idle"); setLeft(timeLimit || 0); setTaken(null);
    if (timeLimit) setDraft("");
  }, [timeLimit]);
  useEffect(() => {
    if (phase !== "running") return undefined;
    if (left <= 0) {
      setPhase("done"); setTaken(timeLimit);
      toast.info("Time's up! Get feedback or reveal the model answer.");
      return undefined;
    }
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, left, timeLimit]);

  const startTimer = () => { setDraft(""); setLeft(timeLimit); setTaken(null); setPhase("running"); };
  const stopTimer = () => { setTaken(timeLimit - left); setPhase("done"); };
  const locked = timed && phase !== "running";
  const canSubmit = draft.trim().length >= 20 && !busy && (!timed || phase === "done");

  const getFeedback = async () => {
    setBusy(true);
    const timing = timed && taken != null ? { time_limit: timeLimit, time_taken: taken } : {};
    try { onFeedback(index, await runJob(`/interview/sets/${setId}/feedback`, { index, answer: draft, ...timing })); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const rate = async (r) => {
    const next = rating === r ? "none" : r;
    try { await api.patch(`/interview/sets/${setId}/rate`, { index, rating: next }); onRate(index, next); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid={`interview-card-${index}`} className="rounded-2xl border border-line bg-surface p-6 fade-up" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="flex items-start gap-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-band font-mono text-xs font-semibold text-white">{index + 1}</span>
        <div className="flex-1">
          <h3 data-testid={`interview-question-${index}`} className="text-lg font-bold leading-snug text-ink">{q.question}</h3>
          {timed && <TimerBar phase={phase} left={left} limit={timeLimit} taken={taken} index={index} onStart={startTimer} onStop={stopTimer} />}
          <Textarea data-testid={`interview-draft-${index}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} maxLength={3000}
            readOnly={locked} disabled={timed && phase === "idle"}
            placeholder={timed && phase === "idle" ? "Start the timer, then type your answer before time runs out" : feedbackLocked ? "Type your answer as you'd say it in the interview, then compare it with the model answer" : "Type your answer as you'd say it in the interview, then get AI feedback (min 20 characters)"} className="mt-4" />
          <div className="mt-3 flex flex-wrap gap-2">
            {feedbackLocked ? <span data-testid={`interview-feedback-locked-${index}`} className="inline-flex items-center rounded-full bg-aqua-soft px-3 py-1.5 text-xs font-semibold text-aqua">AI feedback is a LearnAI Pro feature</span> : (
            <Button data-testid={`interview-feedback-btn-${index}`} disabled={!canSubmit} onClick={getFeedback} className="rounded-full">
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reviewing your answer...</> : <><Wand2 className="mr-2 h-4 w-4" />{feedback ? "Get feedback again" : "Get AI feedback"}</>}
            </Button>)}
            {!open && !(timed && phase === "running") && <Button data-testid={`interview-reveal-${index}`} variant="outline" onClick={() => setOpen(true)} className="rounded-full"><Eye className="mr-2 h-4 w-4" />Reveal model answer</Button>}
          </div>
          {feedback && <FeedbackPanel fb={feedback} index={index} />}
          {open && (
            <div className="mt-4 flex flex-col gap-4">
              <div data-testid={`interview-answer-${index}`} className="rounded-xl border-l-4 border-aqua bg-aqua-soft/50 p-4 text-sm leading-relaxed text-ink-2">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-aqua">Model answer</p>{q.model_answer}
              </div>
              {q.key_points.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">What interviewers listen for</p>
                  <ul className="space-y-1.5">{q.key_points.map((k, i) => <li key={i} className="flex gap-2 text-sm text-ink-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-aqua-solid" />{k}</li>)}</ul>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {q.follow_up && <p className="flex gap-2 rounded-lg border border-line p-3 text-sm text-ink-2"><MessageSquareQuote className="h-4 w-4 shrink-0 text-brand" /><span><b>Likely follow-up:</b> {q.follow_up}</span></p>}
                {q.tip && <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900"><Lightbulb className="h-4 w-4 shrink-0" /><span><b>Tip:</b> {q.tip}</span></p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`interview-confident-${index}`} size="sm" onClick={() => rate("confident")}
                  className={`rounded-full ${rating === "confident" ? "bg-emerald-700 hover:bg-emerald-800" : "border border-emerald-300 bg-surface text-emerald-700 hover:bg-emerald-50"}`}>
                  <ThumbsUp className="mr-2 h-3.5 w-3.5" />I'd nail this
                </Button>
                <Button data-testid={`interview-practise-${index}`} size="sm" onClick={() => rate("practise")}
                  className={`rounded-full ${rating === "practise" ? "bg-amber-500 hover:bg-amber-600" : "border border-amber-300 bg-surface text-amber-800 hover:bg-amber-50"}`}>
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />Practise more
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
